import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from "../../config/googleMaps.js";
import { loadGoogleMapsLibrary } from "../../utils/loadGoogleMaps.js";
import { OPEN_CONSENT_EVENT } from "../../utils/consent.js";
import { useConsent } from "../../utils/useConsent.js";

const GERMANY_CENTER = { lat: 51.1657, lng: 10.4515 };

// Shared with EventList.jsx's map legend, so the legend swatches and the
// actual pin colors can never drift apart.
export const MARKER_COLORS = {
  own: "#ce9532", // var(--color-gold)
  external: "#167b8a", // teal accent, already used elsewhere in the brand palette (Verhaltenskodex.jsx)
};

function buildInfoContent(f, lang, t) {
  const wrapper = document.createElement("div");
  wrapper.style.cssText = "font-family:inherit; max-width:220px;";
  const title = document.createElement("strong");
  title.textContent = f.title;
  const location = document.createElement("div");
  location.style.cssText = "font-size:13px; margin:4px 0; color:#333;";
  location.textContent = f.location;
  const link = document.createElement("a");
  link.href = `/${lang}/events/${f.slug}`;
  link.style.cssText = "font-size:13px;";
  link.textContent = t("events.detailsCta");
  wrapper.append(title, location, link);
  return wrapper;
}

/**
 * Only ever mounted when the user actively switches to the "Karte" view
 * (see EventList.jsx) — never on page load or the default List view. The
 * Maps JavaScript API bills per map load past a monthly free quota, so
 * this on-demand-only loading is the main cost control; see
 * konzepte/GOOGLE_MAPS_SETUP.md for the full picture (API key
 * restrictions, budget alert, quota cap).
 */
export default function EventsMap({ events, lang }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [error, setError] = useState(false);
  const consent = useConsent();
  // A one-off "show it now" that deliberately does NOT write a stored
  // choice. Loading the map from this button is consent for this view;
  // silently recording a permanent decision — including an implicit "no"
  // to analytics and marketing, which would dismiss the banner — is not
  // something a click labelled "load map" should do. The placeholder
  // offers the cookie settings for the lasting choice instead.
  const [loadOnce, setLoadOnce] = useState(false);
  const mayLoad = consent.maps || loadOnce;

  useEffect(() => {
    if (!mayLoad) return undefined;
    if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID || !containerRef.current) return undefined;
    let cancelled = false;
    let markers = [];

    // Google calls this global on key/referrer/billing problems — a
    // separate mechanism from a rejected promise, so the try/catch below
    // alone doesn't always catch it (the script can "load" successfully
    // and only fail once it actually authenticates).
    window.gm_authFailure = () => {
      console.error(
        "EventsMap: Google Maps auth failure — check API key restrictions (HTTP referrer, API restriction) and that billing is enabled, see konzepte/GOOGLE_MAPS_SETUP.md"
      );
      if (!cancelled) setError(true);
    };

    (async () => {
      try {
        const [{ Map, InfoWindow }, { LatLngBounds }, { AdvancedMarkerElement, PinElement }] = await Promise.all([
          loadGoogleMapsLibrary("maps"),
          loadGoogleMapsLibrary("core"),
          loadGoogleMapsLibrary("marker"),
        ]);
        if (cancelled || !containerRef.current) return;

        const map = new Map(containerRef.current, {
          center: GERMANY_CENTER,
          zoom: 6,
          streetViewControl: false,
          mapTypeControl: false,
          mapId: GOOGLE_MAPS_MAP_ID,
        });

        const withCoords = events.filter((e) => e.frontmatter.coordinates?.length === 2);
        if (withCoords.length === 0) return;

        const infoWindow = new InfoWindow();
        const bounds = new LatLngBounds();

        markers = withCoords.map((event) => {
          const f = event.frontmatter;
          const position = { lat: f.coordinates[0], lng: f.coordinates[1] };
          bounds.extend(position);
          const color = f.externalOrga ? MARKER_COLORS.external : MARKER_COLORS.own;
          const pin = new PinElement({ background: color, borderColor: color, glyphColor: "#0a0907" });
          const marker = new AdvancedMarkerElement({ map, position, title: f.title, content: pin.element });
          marker.addListener("click", () => {
            infoWindow.setContent(buildInfoContent(f, lang, t));
            infoWindow.open({ map, anchor: marker });
          });
          return marker;
        });

        if (withCoords.length === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(11);
        } else {
          map.fitBounds(bounds, 48);
        }
      } catch (err) {
        // Google Maps almost always logs a specific typed error itself
        // (RefererNotAllowedMapError / ApiNotActivatedMapError /
        // BillingNotEnabledMapError / InvalidKeyMapError, etc.) — surface
        // ours too so the real cause shows up in devtools instead of just
        // the generic fallback text.
        console.error("EventsMap: failed to load Google Maps", err);
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      // AdvancedMarkerElement has no setMap() — removal is done by
      // clearing its `map` property directly.
      markers.forEach((marker) => {
        marker.map = null;
      });
    };
  }, [events, lang, t, mayLoad]);

  if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID) {
    return <p style={{ fontSize: "14px", color: "var(--color-cream-soft)" }}>{t("events.mapNotConfigured")}</p>;
  }
  if (error) {
    return <p style={{ fontSize: "14px", color: "var(--color-cream-soft)" }}>{t("events.mapError")}</p>;
  }

  // Nothing reaches Google until this is past: the placeholder makes no
  // request at all, which is the whole point of the "Karten" consent
  // category. Before this existed the map loaded as soon as the view was
  // switched, while the privacy policy stated the opposite.
  if (!mayLoad) {
    return (
      <div
        style={{
          width: "100%",
          height: "520px",
          borderRadius: "14px",
          border: "1px solid rgba(206,149,50,0.35)",
          background: "rgba(254,250,239,0.04)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "18px",
          padding: "32px",
          textAlign: "center",
        }}
      >
        <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.6, maxWidth: "44ch", color: "var(--color-cream-soft)" }}>
          {t("events.mapConsentBody")}
        </p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
          <button type="button" className="tl-pill tl-pill-solid" onClick={() => setLoadOnce(true)}>
            {t("events.mapConsentLoad")}
          </button>
          <button
            type="button"
            className="tl-pill tl-pill-outline"
            onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}
          >
            {t("events.mapConsentSettings")}
          </button>
        </div>
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: "100%", height: "520px", borderRadius: "14px" }} />;
}
